/**
 * "Clicking Tickets in the sidebar clears the search box" (Greg,
 * 2026-07-30).
 *
 * The agent inbox persists its search term with the rest of the filter
 * set, so landing on /tickets normally re-runs whatever query the box
 * was left holding. That is the right behavior for browser Back - an
 * agent who opened a ticket out of a result list expects the list back
 * when they return - but the wrong behavior for the sidebar item, which
 * reads as "take me to my tickets", not "re-run my last search".
 *
 * The two cases only differ by intent, not by URL, so the sidebar
 * attaches a signal to the history entry it pushes and the list pages
 * clear their search box when they see it. Every other arrival at
 * /tickets (Back, a bookmark, a redirect) carries no signal and is left
 * alone.
 *
 * The signal is consumed exactly once: after the callback runs we
 * replace the history entry with a state-less copy. Without that, an
 * agent who clicked Tickets, typed a NEW search, opened a hit and
 * pressed Back would land on the same entry, re-read the signal, and
 * lose the search they just typed.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/** Key inside the history entry's state object. */
const CLEAR_SEARCH_STATE_KEY = "clearSearch";

/**
 * Build the history state that asks a list page to clear its search.
 *
 * The value is a nonce rather than `true` so two clicks in a row look
 * like two distinct signals to the consuming effect.
 */
export function clearSearchNavState(): Record<string, number> {
  return { [CLEAR_SEARCH_STATE_KEY]: Date.now() };
}

function readClearSearchToken(state: unknown): number | undefined {
  if (!state || typeof state !== "object") return undefined;
  const token = (state as Record<string, unknown>)[CLEAR_SEARCH_STATE_KEY];
  return typeof token === "number" ? token : undefined;
}

/**
 * Run `onClear` when the current navigation carries the clear-search
 * signal. `onClear` is read through a ref so callers can pass an inline
 * closure over fresh state without re-arming the effect.
 */
export function useClearSearchSignal(onClear: () => void): void {
  const location = useLocation();
  const navigate = useNavigate();
  const onClearRef = useRef(onClear);
  onClearRef.current = onClear;

  const token = readClearSearchToken(location.state);
  const { pathname, search } = location;

  useEffect(() => {
    if (token === undefined) return;
    onClearRef.current();
    navigate(`${pathname}${search}`, { replace: true, state: null });
  }, [token, pathname, search, navigate]);
}
